<template>
  <div class="actions__container">
    <SegmentControls
      :controls="controls"
      class="is-secondary"
      @change="viewChange"
    />
    <PrimeVueSelect
      v-model="store.selectedSortOption"
      type="select"
      name="sort-by"
      variant="filled"
      class="d-flex align-self-end sort-option"
      :placeholder="placeholderLabel"
      :options="sortOptions"
      option-value="value"
      option-label="label"
      @value-change="handleSortChange(store.selectedSortOption)"
    />
  </div>
</template>

<script setup lang="ts">
import { SegmentControls } from '@hypercodestudio/basler-components';
import type { controlBtn } from '@hypercodestudio/basler-components/dist/components/elements/SegmentControls.vue';
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';
import { PRODUCT_LIST_VIEW_KEY } from '~/stores/productFilterStore/constants';
import type { IProductList } from '~/lib/ContentfulService';
import { isDefined } from '~/utils/guards/isDefined';
import { SORTOPTION_PLACEHOLDER } from '~/utils/shop/constants';

type Props = {
  placeholderLabel: string;
};

defineProps<Props>();

const store = useProductFilterStore();

const { $textDictionary } = useNuxtApp();

const controls = computed(() => {
  return [
    {
      id: 'grid',
      title:
        $textDictionary.value['productsLists.viewControls.grid.title'] ??
        'Grid',
      isActive: store.view === 'grid',
      iconName: 'grid-2x2'
    },
    {
      id: 'table-list',
      title:
        $textDictionary.value['productsLists.viewControls.tableList.title'] ??
        'List',
      isActive: store.view === 'table-list',
      iconName: 'list-square'
    }
  ];
});

type FormKitOptionItem = { value: string; label: string };

const sortOptions = computed((): FormKitOptionItem[] => {
  const options: FormKitOptionItem[] = [];
  const sortDirection = [
    { value: 'asc', icon: '↑' },
    { value: 'desc', icon: '↓' }
  ];
  store.sortOptions.forEach((option) => {
    sortDirection.forEach((direction) => {
      if (isDefined(option.label) && isDefined(option.value)) {
        options.push({
          label: option.label + ' ' + direction.icon,
          value: option.value + SORTOPTION_PLACEHOLDER + direction.value
        });
      }
    });
  });
  return options;
});

function viewChange(view: controlBtn) {
  window.localStorage.setItem(PRODUCT_LIST_VIEW_KEY, view.id);
  store.changeView(view.id as NonNullable<IProductList['fields']['view']>);
}

function handleSortChange(val: string | undefined) {
  const [option, direction] = val?.split('__') ?? ['position', 'asc'];
  store.changeSort(option ?? 'position');
  store.toggleSortDirection(direction as 'asc' | 'desc');
}
</script>

<style>
.actions__container {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.sort-option {
  min-width: 190px;
}
</style>
